import { forwardRef, useId } from 'react';

import { cx } from 'utils';

/**
 * Textarea — multi-line field styled to match `Input`.
 * forwardRef so it plugs straight into React Hook Form's `register()`.
 */
const Textarea = forwardRef(function Textarea(
  { label, hint, error, rows = 4, className = '', containerClassName = '', id, ...props },
  ref
) {
  const autoId = useId();
  const fieldId = id || autoId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  const field = cx(
    'w-full resize-y bg-white text-slate-900 placeholder:text-slate-400',
    // slate-300 not slate-200: a field needs a boundary you can actually see on
    // white (#E2E8F0 reads as barely-there at 1.2:1).
    'rounded-xl border border-slate-300 px-4 py-3 text-sm leading-relaxed transition-all duration-200 ease-premium',
    'hover:border-slate-400',
    'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25',
    'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60',
    error && 'border-danger focus:border-danger focus:ring-danger/25',
    className
  );

  return (
    <div className={cx('w-full', containerClassName)}>
      {label && (
        <label htmlFor={fieldId} className="mb-2 block text-sm font-semibold text-slate-800">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={field}
        {...props}
      />
      {error ? (
        <p id={`${fieldId}-error`} className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export default Textarea;
